import React, { useState, useEffect } from 'react';
import { jobsAPI, clientsAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import DocumentAttachments from '../components/DocumentAttachments';
import { Plus, Edit2, Trash2, Loader2, Briefcase, Paperclip } from 'lucide-react';

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    job_number: '',
    name: '',
    client_id: '',
    description: '',
    bill_rate: '',
    unit_type: 'days',
    hourly_rate: '',
    daily_rate: '',
    accommodation_rate: '',
    km_rate: '',
    return_trip_rate: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDocuments, setShowDocuments] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, clientsRes] = await Promise.all([
        jobsAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setJobs(jobsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        job_number: job.job_number,
        name: job.name,
        client_id: job.client_id,
        description: job.description || '',
        bill_rate: job.bill_rate || '',
        unit_type: job.unit_type || 'days',
        hourly_rate: job.hourly_rate || '',
        daily_rate: job.daily_rate || '',
        accommodation_rate: job.accommodation_rate || '',
        km_rate: job.km_rate || '',
        return_trip_rate: job.return_trip_rate || '',
        is_active: job.is_active
      });
    } else {
      setEditingJob(null);
      setFormData({
        job_number: '',
        name: '',
        client_id: '',
        description: '',
        bill_rate: '',
        unit_type: 'days',
        hourly_rate: '',
        daily_rate: '',
        accommodation_rate: '',
        km_rate: '',
        return_trip_rate: '',
        is_active: true
      });
    }
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
    setShowDocuments(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const data = {
        ...formData,
        bill_rate: parseFloat(formData.bill_rate) || 0,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        accommodation_rate: parseFloat(formData.accommodation_rate) || 0,
        km_rate: parseFloat(formData.km_rate) || 0,
        return_trip_rate: parseFloat(formData.return_trip_rate) || 0
      };

      if (editingJob) {
        await jobsAPI.update(editingJob.id, data);
      } else {
        await jobsAPI.create(data);
      }
      
      closeModal();
      fetchData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await jobsAPI.delete(id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Projects</h1>
            <p className="mt-1 text-slate-500">Manage projects and project numbers</p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-job-btn"
          >
            <Plus className="w-4 h-4" />
            Add Project
          </button>
        </div>

        {/* Projects Table */}
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left">Project Number</th>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Client</th>
                  <th className="px-6 py-3 text-right">Bill Rate</th>
                  <th className="px-6 py-3 text-center">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      No projects found. Create your first job to get started.
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr key={job.id} className="table-row">
                      <td className="px-6 py-4 font-mono text-sm font-medium text-slate-900">
                        {job.job_number}
                      </td>
                      <td className="px-6 py-4 text-slate-900">{job.name}</td>
                      <td className="px-6 py-4 text-slate-600">{getClientName(job.client_id)}</td>
                      <td className="px-6 py-4 text-right font-mono text-slate-600">
                        ${job.bill_rate?.toLocaleString() || '0'}/{job.unit_type === 'hours' ? 'hr' : 'day'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.is_active 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : 'bg-slate-50 text-slate-500 border border-slate-200'
                        }`}>
                          {job.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openModal(job)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                            data-testid={`edit-job-${job.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(job.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                            data-testid={`delete-job-${job.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              {editingJob ? 'Edit Project' : 'Add Project'}
            </h3>
            
            {/* Tabs for Edit mode */}
            {editingJob && (
              <div className="flex border-b border-slate-200 mb-4">
                <button
                  type="button"
                  onClick={() => setShowDocuments(false)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    !showDocuments 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Details
                </button>
                <button
                  type="button"
                  onClick={() => setShowDocuments(true)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1 ${
                    showDocuments 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Paperclip className="w-4 h-4" />
                  Documents
                </button>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            
            {/* Documents Tab */}
            {showDocuments && editingJob ? (
              <div className="py-2">
                <DocumentAttachments
                  entityType="project"
                  entityId={editingJob.id}
                />
              </div>
            ) : (
              /* Details Form */
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Project Number *
                  </label>
                  <input
                    type="text"
                    value={formData.job_number}
                    onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
                    required
                    className="input-base font-mono"
                    placeholder="AGRN-001"
                    data-testid="job-number-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Client *
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    required
                    className="input-base"
                  >
                    <option value="">Select client...</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-base"
                  data-testid="job-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="input-base resize-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bill Rate ($)
                  </label>
                  <input
                    type="number"
                    value={formData.bill_rate}
                    onChange={(e) => setFormData({ ...formData, bill_rate: e.target.value })}
                    step="0.01"
                    min="0"
                    className="input-base font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit Type
                  </label>
                  <select
                    value={formData.unit_type}
                    onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                    className="input-base"
                  >
                    <option value="days">Days</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
              </div>
              
              {/* Additional Rate Settings */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Additional Rate Settings</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Daily Rate ($)
                    </label>
                    <input
                      type="number"
                      value={formData.daily_rate}
                      onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                      step="0.01"
                      min="0"
                      className="input-base font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Hourly Rate ($)
                    </label>
                    <input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                      step="0.01"
                      min="0"
                      className="input-base font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Accommodation Rate ($/day)
                    </label>
                    <input
                      type="number"
                      value={formData.accommodation_rate}
                      onChange={(e) => setFormData({ ...formData, accommodation_rate: e.target.value })}
                      step="0.01"
                      min="0"
                      className="input-base font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Km Rate ($/km)
                    </label>
                    <input
                      type="number"
                      value={formData.km_rate}
                      onChange={(e) => setFormData({ ...formData, km_rate: e.target.value })}
                      step="0.01"
                      min="0"
                      className="input-base font-mono"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Return Trip Rate ($)
                    </label>
                    <input
                      type="number"
                      value={formData.return_trip_rate}
                      onChange={(e) => setFormData({ ...formData, return_trip_rate: e.target.value })}
                      step="0.01"
                      min="0"
                      className="input-base font-mono"
                      placeholder="0.00"
                      data-testid="return-trip-rate-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-[#194eb2] focus:ring-[#194eb2] border-slate-300 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">
                  Active job (available for timesheet entry)
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-job-btn"
                >
                  {saving ? 'Saving...' : editingJob ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default JobsPage;
